/**
 * People — manage clients & uplines outside the one-time setup wizard.
 *
 * List + search + filter (role / status), add a client or upline anytime,
 * edit name/notes, archive/restore, delete (with blocker pre-check), and jump
 * to a party's ledger. The `self` party is never listed here.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, Pencil, Plus, Search, Users } from 'lucide-react';
import {
  api,
  ApiError,
  similarFromError,
  type Party,
  type PartyRole,
  type SimilarParty,
} from '../api';
import { AppShell, useReadOnly } from '../ui/AppShell';
import { PageHeader } from '../ui/PageHeader';
import { Btn, EmptyState, SectionCard, StatePill, StatTile } from '../ui/components';
import { Button, ErrorBanner, Spinner, TextArea, TextInput } from '../ui/primitives';
import { Modal } from '../ui/Modal';
import { EditPartyDialog } from '../parties/EditPartyDialog';
import { EntityManageMenu } from '../components/EntityManageMenu';

type RoleFilter = 'all' | 'client' | 'upline';
type StatusFilter = 'active' | 'archived' | 'all';

export function PeoplePage() {
  const readOnly = useReadOnly();
  const qc = useQueryClient();

  const partiesQ = useQuery<Party[]>({
    queryKey: ['parties'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [createRole, setCreateRole] = useState<PartyRole | null>(null);
  const [editing, setEditing] = useState<Party | null>(null);

  const people = useMemo(
    () => (partiesQ.data ?? []).filter((p) => p.role !== 'self'),
    [partiesQ.data],
  );
  const activeClients = people.filter((p) => p.role === 'client' && p.status === 'active').length;
  const activeUplines = people.filter((p) => p.role === 'upline' && p.status === 'active').length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return people
      .filter((p) => (roleFilter === 'all' ? true : p.role === roleFilter))
      .filter((p) => (statusFilter === 'all' ? true : p.status === statusFilter))
      .filter((p) => (q ? p.displayName.toLowerCase().includes(q) : true))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [people, roleFilter, statusFilter, search]);

  return (
    <AppShell
      title="People"
      toolbar={
        <PageHeader
          section="people"
          title="People"
          subtitle="Clients & uplines"
          actions={
            !readOnly ? (
              <>
                <Btn size="sm" onClick={() => setCreateRole('client')}>
                  <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                  <span className="hidden sm:inline">Add client</span>
                  <span className="sm:hidden">Client</span>
                </Btn>
                <Btn size="sm" variant="secondary" onClick={() => setCreateRole('upline')}>
                  <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                  <span className="hidden sm:inline">Add upline</span>
                  <span className="sm:hidden">Upline</span>
                </Btn>
              </>
            ) : undefined
          }
        />
      }
    >
      <div className="space-y-4">
        {/* KPI tiles */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <StatTile section="people" variant="hero" icon={Users} label="People" value={String(people.length)} subtext="clients + uplines" />
          <StatTile section="people" label="Clients" value={String(activeClients)} subtext="active" />
          <StatTile section="people" label="Uplines" value={String(activeUplines)} subtext="active" />
        </div>

        {/* Search + filters */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" strokeWidth={2} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search people…"
              className="w-full min-h-[40px] rounded-xl border border-border bg-surface pl-9 pr-3 text-body text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
          </label>
          <div className="flex gap-2">
            <FilterTabs<RoleFilter>
              value={roleFilter}
              onChange={setRoleFilter}
              options={[
                { v: 'all', label: 'All' },
                { v: 'client', label: 'Clients' },
                { v: 'upline', label: 'Uplines' },
              ]}
            />
            <FilterTabs<StatusFilter>
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { v: 'active', label: 'Active' },
                { v: 'archived', label: 'Archived' },
                { v: 'all', label: 'All' },
              ]}
            />
          </div>
        </div>

        {/* List */}
        {partiesQ.isLoading ? (
          <div className="flex items-center gap-2 px-1 py-6 text-small text-ink-muted">
            <Spinner /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            section="people"
            icon={Users}
            title="No people here"
            description="Add a client or upline, or switch the filters."
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((p) => (
              <SectionCard key={p.id} section="people">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-h2 font-semibold text-ink">{p.displayName}</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <StatePill state={p.role} />
                      {p.status === 'archived' && <StatePill state="archived" />}
                    </div>
                    {p.notes && (
                      <p className="mt-2 line-clamp-2 text-caption text-ink-muted">{p.notes}</p>
                    )}
                  </div>
                  <Link
                    to={`/parties/${p.id}/ledger`}
                    className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-surface px-3 py-1.5 text-small font-medium text-sec-people transition-colors hover:bg-surface-raised"
                  >
                    Ledger
                    <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                  </Link>
                </div>

                {!readOnly && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(p)}>
                      <Pencil className="h-3.5 w-3.5" strokeWidth={2} /> Edit
                    </Button>
                    <EntityManageMenu
                      kind="parties"
                      id={p.id}
                      noun={p.role}
                      isArchived={p.status === 'archived'}
                      archivePinGated
                      onChanged={() => void qc.invalidateQueries({ queryKey: ['parties'] })}
                    />
                  </div>
                )}
              </SectionCard>
            ))}
          </div>
        )}
      </div>

      {createRole && (
        <CreatePartyDialog
          role={createRole}
          existing={people}
          onClose={() => setCreateRole(null)}
          onCreated={() => {
            setCreateRole(null);
            void qc.invalidateQueries({ queryKey: ['parties'] });
          }}
        />
      )}
      {editing && (
        <EditPartyDialog party={editing} onClose={() => setEditing(null)} onSaved={() => setEditing(null)} />
      )}
    </AppShell>
  );
}

function FilterTabs<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { v: T; label: string }[];
}) {
  return (
    <div role="tablist" className="inline-flex rounded-xl border border-border bg-surface-raised p-1">
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.v)}
            className={[
              'min-h-[36px] rounded-lg px-3 py-1 text-xs font-medium transition-colors',
              active ? 'bg-sec-people text-white' : 'text-ink-muted hover:text-ink',
            ].join(' ')}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function CreatePartyDialog({
  role,
  existing,
  onClose,
  onCreated,
}: {
  role: PartyRole;
  existing: Party[];
  onClose: () => void;
  onCreated: (p: Party) => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Similar-name warning (Phase 4c). Warn only — "Create anyway" resubmits
  // with allowSimilar:true; nothing is ever auto-matched or blocked.
  const [similar, setSimilar] = useState<SimilarParty[] | null>(null);

  const dup = existing.some(
    (p) => p.role === role && p.displayName.trim().toLowerCase() === displayName.trim().toLowerCase(),
  );

  const createMut = useMutation({
    mutationFn: (allowSimilar: boolean) =>
      api.post<Party>('/api/parties', {
        role,
        displayName: displayName.trim(),
        notes: notes.trim().length === 0 ? null : notes.trim(),
        allowSimilar,
      }),
    onSuccess: (p) => onCreated(p),
    onError: (e) => {
      const matches = similarFromError<SimilarParty>(e);
      if (matches) {
        setSimilar(matches);
        return;
      }
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const noun = role === 'client' ? 'client' : 'upline';

  return (
    <Modal onClose={onClose} size="sm">
        <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
          <h2 className="text-small font-semibold capitalize text-ink">Add {noun}</h2>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </header>
        <form
          className="flex-1 overflow-y-auto p-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            setSimilar(null);
            if (!displayName.trim()) {
              setError('Name is required.');
              return;
            }
            createMut.mutate(false);
          }}
        >
          <div className="space-y-3">
            <TextInput
              label="Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              name="display-name"
              autoFocus
            />
            {dup && (
              <p className="text-caption text-warn">
                A {noun} named “{displayName.trim()}” already exists. Adding this makes a separate one.
              </p>
            )}
            <TextArea
              label="Notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              hint="Optional. Metadata only."
            />
          </div>
          {similar && similar.length > 0 && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="text-small font-medium text-amber-800">
                Similar {similar.length === 1 ? 'name' : 'names'} already{' '}
                {similar.length === 1 ? 'exists' : 'exist'}:{' '}
                {similar.map((s) => s.displayName).join(', ')}
              </p>
              <p className="mt-1 text-caption text-amber-700">
                Same person? Close this and use them instead of creating a
                duplicate. Otherwise:
              </p>
              <div className="mt-2">
                <Button
                  type="button"
                  disabled={createMut.isPending}
                  onClick={() => {
                    setSimilar(null);
                    createMut.mutate(true);
                  }}
                >
                  {createMut.isPending ? <Spinner /> : 'Create anyway'}
                </Button>
              </div>
            </div>
          )}
          {error && (
            <div className="mt-3">
              <ErrorBanner>{error}</ErrorBanner>
            </div>
          )}
          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? <Spinner /> : `Add ${noun}`}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
    </Modal>
  );
}
